import { Link } from "react-router-dom";
import { Helmet, HelmetData } from "react-helmet-async";
import {
  AI_BG_NEW,
  Our_Product_Bg,
  Speedata_Apache_Gif,
  Speedata_Business_Case_Img,
  Speedata_Hero_Img,
  Speedata_Inference_Icon,
  Speedata_Model_Icon,
  Speedata_Solution_Img,
  Speedata_Unified_Icon,
} from "../../assets";
import { useModal } from "../../utils/demoProvider";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  riseFrom,
  maskedLineReveal,
} from "../../utils/animations";

const trends = [
  {
    number: "01",
    title: "Connect your existing data infrastructure",
    description:
      "Bring your existing data lakes, warehouses, and streaming feeds. [AI] Platform's ingestion layer handles batch and real-time sources without requiring changes to your data architecture or existing pipelines.",
  },
  {
    number: "02",
    title: "Run Spark on APU, without code changes.",
    description:
      "Submit your existing Spark jobs without modification. The APU hardware handles query optimisation, data shuffling, and processing at silicon speed; transparently, below the application layer. Your teams continue working as they do today, with dramatically different results.",
  },
  {
    number: "03",
    title: "Scale on demand. Pay only for consumption.",
    description:
      "Spin up dedicated or shared APU clusters as workloads grow. No capacity planning cycles. No over-provisioning cost. Enterprise SLAs with committed uptime and dedicated infrastructure options.",
  },
];

const data = [
  {
    icon: Speedata_Model_Icon,
    title: "Model Training",
    desc: "Dedicated GPU capacity for training and fine-tuning analytical models at enterprise scale.",
  },
  {
    icon: Speedata_Inference_Icon,
    title: "Model Inference",
    desc: "Low-latency, high-throughput inference for production AI applications and autonomous agents.",
  },
  {
    icon: Speedata_Unified_Icon,
    title: "Unified Billing",
    desc: "APU compute and GPU capacity on a single subscription. One vendor relationship. One support contract.",
  },
];

const deploymentComparison = [
  {
    feature: "Best for",
    cloud:
      "Organisations seeking maximum performance with zero infrastructure overhead",
    onPremise:
      "Organisations with data sovereignty, regulatory, or compliance mandates (financial services, government, healthcare, defence)",
  },
  {
    feature: "Compute",
    cloud: "On-demand APU + GPU clusters, fully managed",
    onPremise: "Dedicated Speedata APU hardware in your data centre",
  },
  {
    feature: "Management",
    cloud: "Fully managed by [AI], provisioning, scaling, monitoring, updates",
    onPremise:
      "[AI] managed software on your hardware, you control physical access and data boundaries",
  },
  {
    feature: "Pricing model",
    cloud: "Base subscription + consumption (compute hours, data volume)",
    onPremise: "APU hardware + annual software licence + managed support",
  },
  {
    feature: "Typical industries",
    cloud: "Technology, e-commerce, SaaS, media",
    onPremise:
      "Financial services, government, healthcare, defence, regulated enterprise",
  },
];

const SpeedataPage = () => {
  const { openModal } = useModal();
  const pageRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        // Hero
        maskedLineReveal(pageRef.current.querySelector(".sd-hero-heading"), {
          scrollTrigger: revealTrigger(".sd-hero-heading"),
        });
        riseFrom(".sd-hero-item", {
          delay: 0.2,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".sd-hero-heading", "top 85%"),
        });
        riseFrom(".sd-hero-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".sd-hero-media"),
        });

        // The Business Case
        riseFrom(".sd-case-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".sd-case-head"),
        });
        riseFrom(".sd-case-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".sd-case-media"),
        });

        // The Solution
        riseFrom(".sd-solution-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".sd-solution-head"),
        });
        riseFrom(".sd-solution-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".sd-solution-media"),
        });

        // How it works
        riseFrom(".sd-how-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".sd-how-head"),
        });
        riseFrom(".sd-how-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".sd-how-media"),
        });
        riseFrom(".sd-trend-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".sd-trend-list", "top 75%"),
        });

        // Deployment
        riseFrom(".sd-deploy-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".sd-deploy-head"),
        });
        riseFrom(".sd-deploy-table", {
          y: 40,
          duration: 1,
          scrollTrigger: revealTrigger(".sd-deploy-table", "top 75%"),
        });

        // GPU Clusters
        riseFrom(".sd-gpu-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".sd-gpu-head"),
        });
        riseFrom(".sd-gpu-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".sd-gpu-grid", "top 75%"),
        });

        // Final CTA
        maskedLineReveal(pageRef.current.querySelector(".sd-cta-heading"), {
          scrollTrigger: revealTrigger(".sd-cta-heading"),
        });
        riseFrom(".sd-cta-copy, .sd-cta-btn", {
          delay: 0.2,
          stagger: 0.15,
          scrollTrigger: revealTrigger(".sd-cta-copy", "top 85%"),
        });
      });
      return () => mm.revert();
    },
    { scope: pageRef }
  );

  const helmetData = new HelmetData({});

  return (
    <main ref={pageRef}>
      <Helmet helmetData={helmetData}>
        <title>
          Speedata — Run Apache Spark up to 80× Faster on APUs
        </title>
        <link rel="canonical" href="https://analyticsintelligence.com/speedata" />
        <meta
          name="description"
          content="Speedata is a managed, APU-powered Apache Spark service by Analytics Intelligence — run Spark up to 80× faster at up to 40% lower cost, with no code changes, in the cloud or on-premise."
        />
        <meta
          name="keywords"
          content="Speedata, APU, Analytics Processing Unit, Apache Spark acceleration, managed Spark service, GPU clusters, NVIDIA GPU, big data analytics, on-premise Spark, cloud analytics, Analytics Intelligence"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta
          property="og:title"
          content="Speedata — Run Apache Spark up to 80× Faster on APUs"
        />
        <meta
          property="og:description"
          content="A managed, APU-powered Apache Spark service — run Spark up to 80× faster at up to 40% lower cost, with no code changes, in the cloud or on-premise."
        />
        <meta
          property="og:url"
          content="https://analyticsintelligence.com/speedata"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta
          name="twitter:title"
          content="Speedata — Run Apache Spark up to 80× Faster on APUs"
        />
        <meta
          name="twitter:description"
          content="A managed, APU-powered Apache Spark service — run Spark up to 80× faster at up to 40% lower cost, with no code changes, in the cloud or on-premise."
        />
        <meta
          name="twitter:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Speedata — APU-Powered Apache Spark Service",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Cloud, On-Premise",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "url": "https://analyticsintelligence.com"
            },
            "description": "Speedata is a managed Apache Spark service by Analytics Intelligence, built with partner Speedata and powered by Analytics Processing Units (APUs). It runs Spark workloads up to 80× faster and up to 40% cheaper with no code changes, and offers NVIDIA GPU clusters for AI model training and inference, deployable in the cloud or on-premise.",
            "url": "https://analyticsintelligence.com/speedata",
            "featureList": ["APU-accelerated Apache Spark", "Up to 80× faster", "Up to 40% lower cost", "No code changes", "NVIDIA GPU clusters", "Cloud or on-premise deployment"]
          }
        `}</script>
      </Helmet>
      {/* Hero Section */}
      <section
        style={{
          backgroundImage: `url("${AI_BG_NEW}")`,
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
        className="min-h-[100vh] flex items-center"
      >
        <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16 md:gap-4 xl:gap-16  pt-[126px] md:pt-[96px] py-[96px]">
          <div className="flex basis-2/4 flex-col flex-wrap gap-[16px] items-center md:items-start">
            <div className="space-y-4 flex flex-col items-center md:items-start">
              <span className="sd-hero-item text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                Speedata
              </span>
              <h2
                className="sd-hero-heading text-[36px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white"
                dangerouslySetInnerHTML={{
                  __html: "Run Apache Spark up to 80× faster",
                }}
              />
              <p className="sd-hero-item text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                Stop waiting hours for Spark workloads to finish. Our managed
                APU-powered Spark service, built with Speedata, delivers up to
                80× faster performance and up to 40% lower infrastructure
                costs, without requiring any code changes.
              </p>
            </div>

            <div className="sd-hero-item flex gap-4">
              <Link to="/contact">
                <button className="text-sm md:text-base font-semibold py-3 px-4 md:px-6 min-w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                  Talk to Sales
                </button>
              </Link>
            </div>
          </div>
          <div className="basis-2/4 flex justify-end">
            <img
              className="sd-hero-media w-full rotate-y-[30deg]"
              src={Speedata_Hero_Img}
              alt="illustration"
            />
          </div>
        </div>
      </section>

      <section className="bg-[#010127] relative pt-[40px] pb-[96px] md:pb-[96px]">
        <div className="container relative">
          <div className="w-[150px] lg:w-[400px] h-[300px] bg-[#0073FF]/50 blur-[150px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] z-[80]"></div>
          <div className="flex flex-col md:flex-row-reverse items-center mt-11 gap-11 md:gap-5 z-[100] relative">
            <div class="sd-case-head flex-1 space-y-4">
              <span className="text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                The Business Case
              </span>
              <h2 class="text-2xl text-center md:text-left md:text-[32px] leading-[120%] font-extrabold text-white">
                Analytics infrastructure that fails to scale is a strategic
                liability.
              </h2>

              <p className="text-sm md:text-base text-white">
                Apache Spark on commodity cloud compute means paying for
                performance you never receive. [AI] Platform fixes it at the
                hardware level, through silicon purpose-built for the analytics.
              </p>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <img
                src={Speedata_Business_Case_Img}
                alt="Data Structures"
                className="sd-case-media w-full h-auto md:max-w-[500px]"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#010127] relative pt-[40px] pb-[96px] md:pb-[96px]">
        <div className="container relative">
          <div className="flex flex-col md:flex-row items-center mt-11 gap-11 md:gap-5 z-[100] relative">
            <div class="sd-solution-head flex-1 space-y-4">
              <span className="text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                The Solution
              </span>
              <h2 class="text-2xl text-center md:text-left md:text-[32px] leading-[120%] font-extrabold text-white">
                Your analytics deserve better than a CPU.
              </h2>

              <p className="text-sm md:text-base text-white">
                [AI] Platform, built with our partner Speedata, moves your
                heaviest analytics operations off general-purpose CPUs and onto
                silicon made for the job. Speedata's Analytics Processing Units
                run analytics workloads directly in hardware, the first chips
                engineered exclusively for data analytics.
              </p>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <img
                src={Speedata_Solution_Img}
                alt="Data Structures"
                className="sd-solution-media w-full h-auto md:max-w-[530px]"
              />
            </div>
          </div>
        </div>
      </section>
      <section className="bg-[#F2EFF3] relative pt-[40px] pb-[96px] md:pb-[96px]">
        <div className="container relative">
          <div className="sd-how-head flex flex-col items-center text-center md:text-left z-[800]">
            <span className="text-sm md:text-base bg-[#C7E0FF] px-3 p-[6px] rounded-[100px] text-black">
              How it works
            </span>
            <h2 className="text-black font-extrabold text-[24px] md:text-[32px] text-center">
              Apache Spark as a service, on APU hardware
            </h2>
          </div>
          <div className="flex flex-col md:flex-row items-center mt-11 gap-11 md:gap-6 lg:gap-8 z-[100] relative">
            <div className="flex-1 flex items-center justify-center">
              <img
                src={Speedata_Apache_Gif}
                alt="Data Structures"
                className="sd-how-media w-full h-auto"
              />
            </div>

            <div className="sd-trend-list flex-1 w-full flex flex-col gap-6">
              {trends.map((trend) => (
                <div
                  key={trend.number}
                  className="sd-trend-card flex items-center gap-5 md:gap-6 bg-white backdrop-blur-sm  p-4 rounded-xl"
                >
                  <span className="text-[#0073FF] font-extrabold text-2xl md:text-3xl lg:text-[44px] leading-none shrink-0">
                    {trend.number}
                  </span>
                  <div className="space-y-1">
                    <h3 className="text-black text-lg md:text-xl font-extrabold leading-tight !mb-0">
                      {trend.title}
                    </h3>
                    <p className="text-black text-sm md:text-base">
                      {trend.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#010127] relative py-[96px]">
        <div className="container relative flex flex-col gap-11 isolate">
          <div className="w-[150px] lg:w-[400px] h-[300px] bg-[#0073FF]/50 blur-[150px] absolute top-[30%] left-[50%] translate-x-[-50%] translate-y-[-50%]"></div>

          <div className="sd-deploy-head flex flex-col items-center md:items-start  z-[1000]">
            <span className="text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
              Deployment
            </span>

            <h2 className="text-white font-extrabold text-[24px] md:text-[32px] text-center md:text-left">
              Your infrastructure. Your deployment model
            </h2>

            <p className="text-white text-sm md:text-base max-w-5xl text-center md:text-left">
              For organisations with regulatory compliance obligations, data
              residency requirements, or data sovereignty mandates, [AI]
              Platform is available as a fully managed on-premise deployment,
              Speedata APU hardware in your own data centre, managed by [AI].
            </p>
          </div>

          <div className="sd-deploy-table mt-0 md:mt-11 z-[1000]">
            {/* Desktop / tablet table */}
            <div className="hidden md:block overflow-hidden">
              {/* Header row */}
              <div className="grid grid-cols-[1.1fr_1.6fr_1.6fr] bg-[#0073FF45]">
                <div className="py-2 px-5 lg:px-6" />
                <div className="py-2 px-5 lg:px-6 text-lg font-bold text-white">
                  Cloud
                </div>
                <div className="py-2 px-5 lg:px-6 text-lg font-bold text-white">
                  On-Premise
                </div>
              </div>

              {/* Body rows */}
              {deploymentComparison.map((row, index) => (
                <div
                  key={row.feature}
                  className={`grid grid-cols-[1.1fr_1.6fr_1.6fr] ${
                    index % 2 === 0 ? "bg-white/8" : "bg-white/5"
                  }`}
                >
                  <div className="py-3 px-5 lg:p-6 text-base font-bold text-white">
                    {row.feature}
                  </div>
                  <div className="py-3 px-5 lg:px-6 text-sm xl:text-base text-[#C9D2E3] leading-relaxed">
                    {row.cloud}
                  </div>
                  <div className="py-3 px-5 lg:px-6 text-sm xl:text-base text-[#C9D2E3] leading-relaxed">
                    {row.onPremise}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile stacked cards */}
            <div className="flex flex-col gap-4 md:hidden">
              {deploymentComparison.map((row) => (
                <div
                  key={row.feature}
                  className="round border border-white/10 bg-white/[0.03] overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-[#0073FF] to-[#0A1E66] px-4 py-3 text-base font-bold text-white">
                    {row.feature}
                  </div>
                  <div className="px-4 py-4 border-b border-white/10">
                    <p className="text-sm font-bold uppercase tracking-wide text-white mb-1">
                      Cloud
                    </p>
                    <p className="text-sm text-[#D2D2D2] leading-relaxed">
                      {row.cloud}
                    </p>
                  </div>
                  <div className="px-4 py-4">
                    <p className="text-sm font-bold uppercase tracking-wide text-white mb-1">
                      On-Premise
                    </p>
                    <p className="text-sm text-[#D2D2D2] leading-relaxed">
                      {row.onPremise}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#010127] relative pt-[48px] pb-[96px] md:py-[96px]">
        <div className="container w-full flex flex-col gap-11">
          <div className="w-[150px] lg:w-[400px] h-[300px] bg-[#0073FF]/50 blur-[150px] absolute top-[30%] left-[50%] translate-x-[-50%] translate-y-[-50%]"></div>

          <div className="sd-gpu-head flex flex-col items-center z-[1000]">
            <span className="text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
              GPU Clusters
            </span>

            <h2 className="text-white font-extrabold text-[24px] md:text-[32px] text-center">
              NVIDIA GPU clusters for AI inference and model training
            </h2>

            <p className="text-white text-sm md:text-base max-w-5xl text-center">
              Beyond Spark analytics, [AI] Platform provides enterprise access
              to NVIDIA inference processors for AI model training and serving.
              Run CORE models or bring your own, under the same managed
              infrastructure, same control plane, and same billing relationship.
            </p>
          </div>

          <div className="sd-gpu-grid mt-0 md:mt-11 gap-6 md:gap-12 grid grid-cols-1 md:grid-cols-12">
            {data.map((item, idx) => (
              <div
                key={idx}
                className="sd-gpu-card flex flex-col items-start gap-3 col-span-12 md:col-span-6 lg:col-span-4 bg-[#40406C80] backdrop-blur-sm p-6 rounded-xl"
              >
                <img
                  src={item.icon}
                  className="max-h-[44px]"
                  alt={item.title}
                />
                <p className="text-white font-extrabold text-lg md:text-xl">
                  {item.title}
                </p>
                <p className="text-white text-base">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="relative pt-[116px] pb-10"
        style={{
          backgroundImage: `url("${Our_Product_Bg}")`,
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="container">
          <div className="bg-[#1E1E4080] backdrop-blur-[20px] flex flex-col items-center gap-6 px-4 md:px-8 py-8 sm:py-12 md:py-14 lg:py-16 xl:py-20 rounded-3xl">
            <div className="flex flex-col items-center">
              <h2
                className="sd-cta-heading text-[32px] md:text-[44px] text-center font-extrabold text-white max-w-[718px]"
                dangerouslySetInnerHTML={{
                  __html: "See what your Spark workloads could become",
                }}
              />
              <p className="sd-cta-copy text-sm md:text-base text-center text-[#FFFFFF] max-w-[900px]">
                Run your existing Spark workloads on [AI] Platform and benchmark
                performance against your current infrastructure. Most enterprise
                teams identify substantial cost and performance gains within a
                single evaluation session.
              </p>
            </div>

            <button
              onClick={openModal}
               className="sd-cta-btn text-sm md:text-base font-semibold py-3 px-4 md:px-6 min-w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2"
            >
              Book a Demo
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SpeedataPage;
